import api from './client';
import { API_BASE_URL } from '@/config';

export interface AnalyticsFilterOptions {
  wbs: string[];
  activityCodes: string[];
  statuses: string[];
}

/**
 * Fetch distinct filter options (WBS, activity codes, statuses) for the Analytics context bar.
 * Used to populate multi-select dropdowns. Optional snapshot_id scopes options to that snapshot.
 */
export const getAnalyticsFilterOptions = async (
  projectId: string,
  snapshotId?: string | null
): Promise<AnalyticsFilterOptions> => {
  const params = new URLSearchParams();
  if (snapshotId) params.set('snapshot_id', snapshotId);
  const qs = params.toString();
  const url = `${API_BASE_URL}/v1/analytics/filters/${projectId}${qs ? `?${qs}` : ''}`;
  const response = await api.get(url);
  return response.data as AnalyticsFilterOptions;
};

/**
 * Fetch project analytics (KPI metrics)
 */
export const getProjectAnalytics = async (projectId: string) => {
  try {
    const response = await api.get(`${API_BASE_URL}/v1/analytics/projects/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching project analytics:', error);
    throw error;
  }
};

/**
 * Fetch schedule analytics. Optional snapshot_id scopes to that import (e.g. baseline).
 */
export const getScheduleAnalytics = async (projectId: string, snapshotId?: string | null) => {
  try {
    const params = new URLSearchParams();
    if (snapshotId) params.set('snapshot_id', snapshotId);
    const qs = params.toString();
    const response = await api.get(`${API_BASE_URL}/v1/analytics/projects/${projectId}/schedule${qs ? `?${qs}` : ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching schedule analytics:', error);
    throw error;
  }
};

/**
 * Fetch resource analytics
 */
export const getResourceAnalytics = async (projectId: string) => {
  try {
    const response = await api.get(`${API_BASE_URL}/v1/analytics/projects/${projectId}/resources`);
    return response.data;
  } catch (error) {
    console.error('Error fetching resource analytics:', error);
    throw error;
  }
};

/**
 * Fetch cost analytics
 */
export const getCostAnalytics = async (projectId: string) => {
  try {
    const response = await api.get(`${API_BASE_URL}/v1/analytics/projects/${projectId}/cost`);
    return response.data;
  } catch (error) {
    console.error('Error fetching cost analytics:', error);
    throw error;
  }
};

/**
 * Fetch risk analytics
 */
export const getRiskAnalytics = async (projectId: string) => {
  try {
    const response = await api.get(`${API_BASE_URL}/v1/analytics/projects/${projectId}/risk`);
    return response.data;
  } catch (error) {
    console.error('Error fetching risk analytics:', error);
    throw error;
  }
};

/**
 * Fetch snapshots list for a project (for trend charts: need ≥2 to show snapshot-history charts).
 */
export const getProjectSnapshots = async (projectId: string) => {
  try {
    const response = await api.get(
      `${API_BASE_URL}/v1/snapshots/${projectId}`
    );
    return (response.data as { snapshots?: Array<{ id: string; uploaded_at?: string; import_run_id?: string }> })?.snapshots ?? [];
  } catch (error) {
    console.error('Error fetching project snapshots:', error);
    return [];
  }
};

/**
 * Fetch per-snapshot trend metrics (critical path length, float, near-critical, logic integrity, phase progress).
 * Requires ≥2 snapshots; returns has_history and points keyed by snapshot/upload date.
 */
export const getSnapshotTrendsMetrics = async (
  projectId: string,
  nearCriticalThresholdDays: number = 10
) => {
  try {
    const params = new URLSearchParams();
    params.set('near_critical_threshold_days', String(nearCriticalThresholdDays));
    const response = await api.get(
      `${API_BASE_URL}/v1/analytics/projects/${projectId}/trends/snapshot-metrics?${params.toString()}`
    );
    return response.data as {
      has_history: boolean;
      points: Array<{
        snapshot_id: string;
        uploaded_at: string | null;
        import_run_id: string;
        metrics: Record<string, unknown>;
      }>;
    };
  } catch (error) {
    console.error('Error fetching snapshot trends:', error);
    throw error;
  }
};


